import { useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { useAdminTeam, useAdminInvitations } from '@/hooks/useAdmin'
import { useAuth } from '@/hooks/useAuth'
import { callEdgeFunction } from '@/lib/edge-functions'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { TeamMemberActions } from '@/components/admin/team-member-actions'
import { InviteForm } from '@/components/admin/invite-form'
import { Plus, Users, Loader2, Clock, RotateCw, X } from 'lucide-react'
import { format, isPast, formatDistanceToNow } from 'date-fns'
import { toast } from 'sonner'
import type { AdminRole } from '@/types/database'

const roleColors: Record<AdminRole, string> = {
  owner: 'bg-purple-100 text-purple-800',
  admin: 'bg-blue-100 text-blue-800',
  editor: 'bg-green-100 text-green-800',
}

const roleOptions = [
  { value: 'editor' as const, label: 'Editor', description: 'Can manage products and view orders' },
  { value: 'admin' as const, label: 'Admin', description: 'Full access except team management' },
  { value: 'owner' as const, label: 'Owner', description: 'Full access including team management' },
]

export function AdminTeamPage() {
  const { members, isLoading, refetch } = useAdminTeam()
  const { invitations, isLoading: invitationsLoading, refetch: refetchInvitations, revokeInvitation } = useAdminInvitations()
  const { user, adminRole, hasRoleLevel, isLoading: authLoading } = useAuth()

  const [isInviteDialogOpen, setIsInviteDialogOpen] = useState(false)
  const [inviteResetKey, setInviteResetKey] = useState(0)
  const [resendingId, setResendingId] = useState<string | null>(null)
  const [revokingId, setRevokingId] = useState<string | null>(null)

  const canManageTeam = hasRoleLevel('admin')

  const availableRoles = roleOptions.filter((role) => {
    if (adminRole === 'owner') return true
    if (adminRole === 'admin') return role.value === 'editor'
    return false
  })

  const handleOpenInviteDialog = () => {
    setInviteResetKey(prev => prev + 1)
    setIsInviteDialogOpen(true)
  }

  const handleInviteSuccess = () => {
    setIsInviteDialogOpen(false)
    refetch()
    refetchInvitations()
  }

  const handleResend = async (invitationEmail: string, invitationRole: string, invitationId: string) => {
    setResendingId(invitationId)
    try {
      const result = await callEdgeFunction('admin-invite', {
        method: 'POST',
        body: {
          email: invitationEmail,
          role: invitationRole,
          origin: window.location.origin,
          resend: true,
        },
        withAuth: true,
      })

      if (result.error) {
        toast.error(result.error)
      } else {
        toast.success('Invitation resent')
        refetchInvitations()
      }
    } catch {
      toast.error('Failed to resend invitation')
    } finally {
      setResendingId(null)
    }
  }

  const handleRevoke = async (id: string) => {
    setRevokingId(id)
    const { error } = await revokeInvitation(id)
    if (error) {
      toast.error(error)
    } else {
      toast.success('Invitation revoked')
    }
    setRevokingId(null)
  }

  if (isLoading || authLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  const activeInvitations = invitations.filter(inv => !isPast(new Date(inv.expires_at)))
  const expiredInvitations = invitations.filter(inv => isPast(new Date(inv.expires_at)))

  return (
    <>
      <Helmet>
        <title>Team | Admin</title>
      </Helmet>
      <div className="space-y-4 sm:space-y-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold">Team</h1>
            <p className="text-muted-foreground mt-1">
              Manage admin access and roles
            </p>
          </div>
          {canManageTeam && (
            <Button className="w-full sm:w-auto" onClick={handleOpenInviteDialog}>
              <Plus className="mr-2 h-4 w-4" />
              Invite Member
            </Button>
          )}
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Team Members</CardTitle>
            <CardDescription>
              {members.length} member{members.length !== 1 ? 's' : ''}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {members.length > 0 ? (
              <div className="space-y-4">
                {members.map((member) => (
                  <div
                    key={member.id}
                    className="rounded-lg border p-4"
                  >
                    <div className="flex items-start gap-3 sm:gap-4">
                      <Avatar className="h-10 w-10">
                        <AvatarFallback>
                          {member.name?.[0] || member.email[0].toUpperCase()}
                        </AvatarFallback>
                      </Avatar>

                      <div className="min-w-0 flex-1">
                        <p className="font-medium truncate">
                          {member.name || member.email}
                          {member.id === user?.id && (
                            <span className="text-muted-foreground ml-2">(you)</span>
                          )}
                        </p>
                        <p className="text-sm text-muted-foreground truncate">
                          {member.email}
                        </p>
                        {member.last_login_at && (
                          <p className="text-xs text-muted-foreground">
                            Last login: {format(new Date(member.last_login_at), 'MMM d, yyyy')}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="mt-3 flex items-center justify-between gap-3 sm:mt-4">
                      <Badge className={roleColors[member.role]}>
                        {member.role}
                      </Badge>

                      {canManageTeam && member.id !== user?.id && adminRole && (
                        <TeamMemberActions member={member} currentUserRole={adminRole} onUpdate={refetch} />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Users className="mx-auto h-12 w-12 text-muted-foreground" />
                <h3 className="mt-4 text-lg font-semibold">No team members</h3>
                <p className="text-muted-foreground mt-1">
                  Invite your first team member to get started
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Pending Invitations */}
        {canManageTeam && !invitationsLoading && (activeInvitations.length > 0 || expiredInvitations.length > 0) && (
          <Card>
            <CardHeader>
              <CardTitle>Pending Invitations</CardTitle>
              <CardDescription>
                {activeInvitations.length} active{expiredInvitations.length > 0 ? `, ${expiredInvitations.length} expired` : ''}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {activeInvitations.map((invitation) => (
                  <div key={invitation.id} className="flex items-center justify-between rounded-lg border p-4 gap-3">
                    <div className="min-w-0 flex-1">
                      <p className="font-medium truncate">{invitation.email}</p>
                      <div className="flex items-center gap-2 mt-1 flex-wrap">
                        <Badge className={roleColors[invitation.role as AdminRole] || 'bg-gray-100 text-gray-800'}>
                          {invitation.role}
                        </Badge>
                        <span className="text-xs text-muted-foreground flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          Expires {formatDistanceToNow(new Date(invitation.expires_at), { addSuffix: true })}
                        </span>
                      </div>
                      {invitation.inviter && (
                        <p className="text-xs text-muted-foreground mt-1">
                          Invited by {invitation.inviter.name || invitation.inviter.email}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleResend(invitation.email, invitation.role, invitation.id)}
                        disabled={resendingId === invitation.id}
                      >
                        {resendingId === invitation.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <RotateCw className="h-4 w-4" />
                        )}
                        <span className="ml-1 hidden sm:inline">Resend</span>
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleRevoke(invitation.id)}
                        disabled={revokingId === invitation.id}
                        className="text-red-600 hover:text-red-700"
                      >
                        {revokingId === invitation.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <X className="h-4 w-4" />
                        )}
                        <span className="ml-1 hidden sm:inline">Revoke</span>
                      </Button>
                    </div>
                  </div>
                ))}

                {expiredInvitations.map((invitation) => (
                  <div key={invitation.id} className="flex items-center justify-between rounded-lg border border-dashed p-4 gap-3 opacity-60">
                    <div className="min-w-0 flex-1">
                      <p className="font-medium truncate">{invitation.email}</p>
                      <div className="flex items-center gap-2 mt-1 flex-wrap">
                        <Badge className={roleColors[invitation.role as AdminRole] || 'bg-gray-100 text-gray-800'}>
                          {invitation.role}
                        </Badge>
                        <span className="text-xs text-amber-600 font-medium">Expired</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleResend(invitation.email, invitation.role, invitation.id)}
                        disabled={resendingId === invitation.id}
                      >
                        {resendingId === invitation.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <RotateCw className="h-4 w-4" />
                        )}
                        <span className="ml-1 hidden sm:inline">Resend</span>
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleRevoke(invitation.id)}
                        disabled={revokingId === invitation.id}
                        className="text-red-600 hover:text-red-700"
                      >
                        {revokingId === invitation.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <X className="h-4 w-4" />
                        )}
                        <span className="ml-1 hidden sm:inline">Revoke</span>
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Invite Dialog */}
      <Dialog open={isInviteDialogOpen} onOpenChange={setIsInviteDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Invite Team Member</DialogTitle>
            <DialogDescription>
              Send an invitation to join your admin team
            </DialogDescription>
          </DialogHeader>
          <InviteForm
            availableRoles={availableRoles}
            onSuccess={handleInviteSuccess}
            resetKey={inviteResetKey}
          />
        </DialogContent>
      </Dialog>
    </>
  )
}
